
<template>
	<div>
		<h6 class="form-title">Contact Information</h6>
		<div class="form-subtitle">
			Ensure your order finds you as fast as possible
		</div>
		<DefaultContactForm
			ref="defaultContactForm"
			:container="$parent.$refs.checkoutWrapper"
			:paymentMethod="paymentMethodSelected"
		/>
		<div v-show="requiresPayment" ref="paymentInfo">
			<h6 class="form-title">Payment Information</h6>
			<div>
				<div class="form-subtitle">
					{{ digitalWalletText }}
				</div>
				<PaymentOptions
					ref="paymentOptions"
					v-model="paymentMethodSelected"
					:options="paymentMethods"
					@methodSelected="selectPaymentMethod"
				/>
			</div>
			<transition-expand>
				<GuestCreditCardForm
					v-show="paymentMethodSelected == 1"
					@formFocused="validate"
				/>
			</transition-expand>
		</div>
	</div>
</template>

<style scoped>
.form-title {
	font-weight: 700;
	margin-bottom: 7px;
	color: var(--gR-base-90);
}

.form-subtitle {
	color: var(--sk-grey3);
	font-size: 11px;
	margin-bottom: 23px;
	min-height: 16px;
}
</style>

<script>
import { eventBus } from "@/eventBus"
import { EventBusEvents } from "@/enums/EventBusEvents"
import { NotificationType } from "@/enums/NotificationType"
import { PaymentType } from "@reef-os/utils/enums/paymentType"
import currencyFilterMixin from "@/mixins/currency-filter-mixin"
import squareFormMixin from "@/mixins/square-form-mixin"
import GuestCreditCardForm from "@/components/checkout/GuestCreditCardForm.vue"
import TransitionExpand from "@/components/common/TransitionExpand.vue"
import PaymentOptions from "@/components/checkout/PaymentOptions.vue"
import DefaultContactForm from "@/components/checkout/DefaultContactForm.vue"

export default {
	name: "GuestContactForm",
	mixins: [currencyFilterMixin, squareFormMixin],
	components: {
		DefaultContactForm,
		GuestCreditCardForm,
		TransitionExpand,
		PaymentOptions
	},
	data: function () {
		return {
			emailRegex:
				/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
			phoneRegex: /^[0-9]{10,15}$/,
			paymentMethodSelected: null,
			applePaySupported: false,
			googlePaySupported: false
		}
	},
	props: {
		requiresPayment: Boolean
	},
	computed: {
		paymentMethods() {
			const methods = []

			if (this.allowedPaymentMethods.includes(PaymentType.CREDIT_CARD)) {
				methods.push({
					text: "Card",
					id: PaymentType.CREDIT_CARD
				})
			}

			if (
				this.googlePaySupported &&
				this.allowedPaymentMethods.includes(PaymentType.GOOGLE_PAY)
			) {
				methods.push({
					icon: "sk-icon-google-pay-brands",
					id: PaymentType.GOOGLE_PAY
				})
			}

			if (
				this.applePaySupported &&
				this.allowedPaymentMethods.includes(PaymentType.APPLE_PAY)
			) {
				methods.push({
					icon: "sk-icon-apple-pay",
					id: PaymentType.APPLE_PAY
				})
			}

			return methods
		},
		digitalWalletText: function () {
			if (this.applePaySupported && this.googlePaySupported) {
				return "Pay with one tap using Apple or Google Pay"
			} else if (this.applePaySupported) {
				return "Pay with one tap using Apple Pay"
			} else if (this.googlePaySupported) {
				return "Pay with one tap using Google Pay"
			}
			return ""
		},
		allowedPaymentMethods() {
			return this.$store.getters.allowedPaymentMethods
		},
		paymentMethodObserver() {
			return this.allowedPaymentMethods && this.paymentMethods
		}
	},
	methods: {
		methodsSupported(supportedMethods) {
			if (supportedMethods.applePay) {
				this.applePaySupported = supportedMethods.applePay === true
			}
			if (supportedMethods.googlePay) {
				this.googlePaySupported = supportedMethods.googlePay === true
			}
		},
		validate() {
			return !this.$refs.defaultContactForm.validate()
		},
		selectPaymentMethod(id) {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Payment Method Set",
				data: {
					id: id
				}
			})
			this.paymentMethodSelected = id
		},
		onCardNonceError(errors) {
			let errorMsg = errors[0].message
			eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
				message: errorMsg,
				type: NotificationType.ERROR
			})
			this.$refs.paymentInfo.scrollIntoView({
				block: "start",
				behavior: "smooth"
			})
		},
		inputEventReceived(inputEvent) {
			switch (inputEvent.eventType) {
				case "errorClassAdded":
					this.$emit("toggleLoader", false)
					break
			}
		},
		isFormValid() {
			return this.validate()
		},
		shouldCreatePaymentRequest() {
			const min_amount = this.$store.getters.minimumOrderAmount
			if (this.$store.getters.price < min_amount) {
				eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
					message: `Delivery Minimum is $${min_amount}`,
					type: NotificationType.ERROR
				})
				this.$store.dispatch("sendMixpanelEvent", {
					msg: "App Minimum Alert"
				})
				this.$store.dispatch("setInPurchaseState", false)
				return false
			}
			return true
		},
		checkout() {
			const isValid = this.validate()
			if (isValid) {
				switch (this.paymentMethodSelected) {
					case PaymentType.CREDIT_CARD: {
						this.clickPurchase()
						break
					}
					case PaymentType.GOOGLE_PAY:
						this.$refs.paymentOptions.$refs.googlePayBtn.click()
						break
					case PaymentType.APPLE_PAY:
						this.$refs.paymentOptions.$refs.applePayBtn.click()
						break
					default: {
						eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
							message: "Please select a payment option",
							type: NotificationType.WARNING
						})
						throw "Missing payment option"
					}
				}
			}
		}
	},
	watch: {
		paymentMethodObserver: {
			immediate: true,
			handler() {
				this.setDefaultPaymentMethod()
			}
		}
	}
}
</script>